import { useNavigate } from "@tanstack/react-router";
import { surfaceBase } from "@web/components/ui";
import { useCallback, useEffect, useState } from "react";
import { SkeletonBox } from "@/components/skeleton";
import { useAuth, useToast } from "@/contexts";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import {
  changePassword,
  getNotificationPreferences,
  type NotificationPreferences,
  updateNotificationPreferences,
  updateProfile,
} from "@/features/settings/api";
import type { UserProfile } from "@/types";
import DangerSection from "../settings/DangerSection";
import HealthConnectionsSection from "../settings/HealthConnectionsSection";
import InvitesSection from "../settings/InvitesSection";

export default function Settings() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const toast = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [username] = useState("");
  const [timezone, setTimezone] = useState("");
  const [saving, setSaving] = useState(false);
  const [pwExpanded, setPwExpanded] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [notifSaving, setNotifSaving] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await getCurrentUserProfile();
      if (error) toast.showAlert("Error", error.message, "error");
      else if (data) {
        setProfile(data);
        setDisplayName(data.displayName ?? `${data.firstName} ${data.lastName}`.trim());
        setTimezone(data.timezone ?? "America/Los_Angeles");
      }
      setLoading(false);
    })();
    (async () => {
      const { data } = await getNotificationPreferences();
      if (data) setNotifPrefs(data);
    })();
  }, [user, toast]);

  async function handleChangePassword() {
    if (pwNew !== pwConfirm) {
      setPwError("Passwords do not match");
      return;
    }
    if (pwNew.length < 8) {
      setPwError("Password must be at least 8 characters");
      return;
    }
    setPwSaving(true);
    setPwError("");
    const { error } = await changePassword({ currentPassword: pwCurrent, newPassword: pwNew });
    if (error) {
      if (error.message.includes("incorrect")) {
        setPwError("Current password is incorrect");
      } else {
        setPwError(error.message);
      }
    } else {
      toast.showAlert("Password changed", "Your password has been updated.", "success");
      setPwExpanded(false);
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    }
    setPwSaving(false);
  }

  const handleAccountDeleted = useCallback(async () => {
    await signOut();
    navigate({ to: "/", replace: true });
  }, [signOut, navigate]);

  async function handleTogglePref(
    key: keyof Pick<
      NotificationPreferences,
      "dailyGoal" | "leaderboard" | "competitionEnd" | "friendRequest"
    >,
  ) {
    if (!notifPrefs) return;
    setNotifSaving(key);
    const newVal = notifPrefs[key] === 1 ? 0 : 1;
    const { data, error } = await updateNotificationPreferences({ [key]: newVal });
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else if (data) {
      setNotifPrefs(data);
    }
    setNotifSaving(null);
  }

  const notifOptions: {
    key: keyof Pick<NotificationPreferences, "competitionEnd" | "friendRequest">;
    label: string;
    desc: string;
  }[] = [
    {
      key: "friendRequest",
      label: "Friend request accepted",
      desc: "When someone accepts your friend request",
    },
    {
      key: "competitionEnd",
      label: "Competition ended",
      desc: "When a competition you're in ends",
    },
  ];

  async function handleSaveProfile() {
    setSaving(true);
    const { error } = await updateProfile({ username, displayName, timezone });
    if (error) {
      if (error.message.includes("taken")) {
        toast.showAlert("Username taken", "That username is already in use.", "error");
      } else {
        toast.showAlert("Error", error.message, "error");
      }
    } else {
      toast.showAlert("Saved", "Profile updated successfully.", "success");
    }
    setSaving(false);
  }

  return (
    <div className="min-h-screen bg-background relative">
      <div className="h-screen overflow-y-auto">
        <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-3xl mx-auto w-full">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate({ to: "/dashboard" })}
              className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] cursor-pointer"
            >
              <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
            </button>
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
              Settings
            </span>
          </div>

          {loading ? (
            <div className="flex flex-col gap-4">
              <SkeletonBox width="100%" height={200} borderRadius={20} />
            </div>
          ) : (
            <>
              <div className={`${surfaceBase} bg-card p-5`}>
                <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                  Profile
                </span>
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="display-name"
                      className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                    >
                      Display Name
                    </label>
                    <input
                      id="display-name"
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your display name"
                      className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="timezone"
                      className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                    >
                      Timezone
                    </label>
                    <select
                      id="timezone"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                    >
                      {Intl.supportedValuesOf("timeZone").map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="bg-aqua rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </div>

              <div className={`${surfaceBase} bg-card p-5`}>
                <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                  Account
                </span>
                <div className="flex flex-col gap-3 mt-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-text text-[0.875rem] font-bold">Email</span>
                    <span className="text-text-muted text-[0.875rem]">{profile?.email ?? ""}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPwExpanded(!pwExpanded);
                      setPwError("");
                    }}
                    className="flex items-center justify-between w-full py-2 cursor-pointer bg-transparent border-0"
                  >
                    <span className="text-text text-[0.875rem] font-bold">Password</span>
                    <span className="text-text-dim text-[0.75rem]">{pwExpanded ? "▲" : "▼"}</span>
                  </button>
                  {pwExpanded && (
                    <div className="flex flex-col gap-3 pt-1 border-t border-[rgba(28,27,46,0.1)]">
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="pw-current"
                          className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                        >
                          Current password
                        </label>
                        <input
                          id="pw-current"
                          type="password"
                          value={pwCurrent}
                          onChange={(e) => setPwCurrent(e.target.value)}
                          placeholder="Enter current password"
                          className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="pw-new"
                          className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                        >
                          New password
                        </label>
                        <input
                          id="pw-new"
                          type="password"
                          value={pwNew}
                          onChange={(e) => setPwNew(e.target.value)}
                          placeholder="Enter new password"
                          className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="pw-confirm"
                          className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                        >
                          Confirm new password
                        </label>
                        <input
                          id="pw-confirm"
                          type="password"
                          value={pwConfirm}
                          onChange={(e) => setPwConfirm(e.target.value)}
                          placeholder="Confirm new password"
                          className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                        />
                      </div>
                      {pwError && (
                        <p className="text-[#cc0000] text-[0.8125rem] font-bold">{pwError}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleChangePassword}
                        disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                        className="bg-aqua rounded-[16px] py-2.5 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer disabled:opacity-50"
                      >
                        {pwSaving ? "Updating..." : "Update password"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <InvitesSection />

              {notifPrefs && (
                <div className={`${surfaceBase} bg-card p-5`}>
                  <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                    Notifications
                  </span>
                  <div className="flex flex-col gap-3 mt-4">
                    {notifOptions.map((opt) => (
                      <div key={opt.key} className="flex items-center justify-between py-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-text text-[0.875rem] font-bold">{opt.label}</span>
                          <span className="text-text-muted text-[0.75rem]">{opt.desc}</span>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={notifPrefs[opt.key] === 1}
                          aria-label={`Toggle ${opt.label} notifications`}
                          onClick={() => handleTogglePref(opt.key)}
                          disabled={notifSaving === opt.key}
                          className={`w-12 h-7 rounded-full border border-text cursor-pointer disabled:opacity-50 transition-colors ${
                            notifPrefs[opt.key] === 1 ? "bg-aqua" : "bg-gray-200"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full bg-white border border-text shadow-[1px_1px_0px_0px_#1c1b2e] transition-transform ${
                              notifPrefs[opt.key] === 1
                                ? "translate-x-[1.35rem]"
                                : "translate-x-[0.2rem]"
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {profile?.orgId && (
                <button
                  type="button"
                  onClick={() => navigate({ to: "/settings/groups" })}
                  className="w-full bg-card rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] p-5 flex items-center justify-between cursor-pointer text-left"
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                      Groups
                    </span>
                    <span className="text-text-muted text-[0.8125rem]">
                      Manage organization groups and members
                    </span>
                  </div>
                  <span className="text-text-dim text-[1.25rem]">→</span>
                </button>
              )}

              {profile?.providerConnections && (
                <HealthConnectionsSection connections={profile.providerConnections} />
              )}

              <div className={`${surfaceBase} bg-card p-5`}>
                <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                  Session
                </span>
                <p className="text-text-muted text-[0.8125rem] mt-2 leading-5">
                  Sign out of your account on this device.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (signingOut) return;
                    setSigningOut(true);
                    toast.showConfirm(
                      "Sign out",
                      "Are you sure you want to sign out? Your step data will be saved.",
                      async () => {
                        try {
                          await signOut();
                        } catch {
                          toast.showAlert(
                            "Sign out failed",
                            "Could not sign out. Please try again.",
                            "error",
                          );
                          setSigningOut(false);
                        }
                      },
                      () => setSigningOut(false),
                    );
                  }}
                  disabled={signingOut}
                  className="mt-4 bg-amber rounded-[16px] py-2.5 px-5 text-text text-[0.875rem] font-extrabold border border-text cursor-pointer disabled:opacity-50"
                >
                  {signingOut ? "Signing out..." : "Sign Out"}
                </button>
              </div>

              <DangerSection
                onDeleted={handleAccountDeleted}
                hasPassword={profile?.hasPassword ?? true}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
